import { useQuery } from 'react-query';
import { useFirebase } from 'components/Firebase/index.js';
import { useOrgId } from 'hooks/index.js';
import { vodUrl } from '../../../../../client-config.js';
import { ITEMS_PER_PAGE } from 'pages/Videos/constants.js';
import { useVideoUploads } from './videoUploadContext.jsx';

const useVideosQuery = ({ filterBy, search, page, pageSize = ITEMS_PER_PAGE, sortBy, sortOrder } = {}, options = {}) => {
  const firebase = useFirebase();
  const orgId = useOrgId();
  const { handleProcessingMediaAsset } = useVideoUploads();

  sortOrder = sortOrder && String(sortOrder).toUpperCase();
  if (sortOrder && !['ASC', 'DESC'].includes(sortOrder)) throw new Error(`Invalid sortOrder ${sortOrder}`);

  return useQuery({
    queryKey: ['videos', orgId, { filterBy, search, page, pageSize, sortBy, sortOrder }],
    queryFn: async () => {
      const idToken = await firebase.getIdToken();
      const offset = (page - 1) * pageSize;

      const basePath = `v1/orgs/${orgId}`;
      const fetchUrl = new URL(`${vodUrl}/${basePath}/videos`);
      let postPayload;

      // If search is in the format of "product:productId", we need to fetch the product
      // and then fetch the videos associated with that product.
      const searchProductId = ((search || '').match(/^product:(.+)/) || [])[1];

      // If search is in the format of "action:actionId", we need to fetch the action
      // and then fetch the videos associated with that action.
      const searchActionId = ((search || '').match(/^action:(.+)/) || [])[1];

      // If search is in the format of "playlist:playlistId", fetch videos belonging to that playlist.
      const searchPlaylistId = ((search || '').match(/^playlist:(.+)/) || [])[1];

      // If search is in the format of "tag:tagValue", we need to fetch the videos that have
      // that tag.
      const searchTagValue = ((search || '').match(/^tag:(.+)/) || [])[1];

      if (searchProductId) {
        // Search via a product id
        fetchUrl.pathname = `${basePath}/products/${searchProductId}`;
      } else if (searchActionId) {
        // Search via an action id
        fetchUrl.pathname = `${basePath}/actions/${searchActionId}`;
      } else if (searchPlaylistId) {
        // Search via a playlist id
        fetchUrl.pathname = `${basePath}/video-playlists/${searchPlaylistId}`;
      } else if (searchTagValue) {
        // Search via a query
        fetchUrl.pathname = `${basePath}/videos/query`;
        postPayload = {
          query: {
            order: [[sortBy || 'created_at', sortOrder || 'DESC']],
            field: {
              tag: searchTagValue,
            },
          },
        };
      } else {
        fetchUrl.search = new URLSearchParams({
          ...(search && { search }),
          ...(filterBy && { filterBy }),
          ...(offset && { offset }),
          ...(pageSize && { pageSize }),
          ...(sortBy && { sortBy }),
          ...(sortOrder && { sortOrder: sortOrder.toUpperCase() }),
        });
      }

      const response = await fetch(fetchUrl.toString(), {
        method: postPayload ? 'POST' : 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        ...(postPayload && { body: JSON.stringify(postPayload) }),
      });

      const { ok, status } = response;

      if (status === 404) return { data: [] };
      if (!ok) throw new Error({ status });

      const body = await response.json();

      if (searchPlaylistId) {
        const playlist = body?.data || body || {};
        const videos = Array.isArray(playlist?.videos) ? playlist.videos : [];

        videos.forEach((video) => {
          video.mediaAssets?.forEach(({ id: mediaAssetId, vendorAssetId, vendorUploadId }) => {
            if (vendorUploadId && !vendorAssetId) {
              handleProcessingMediaAsset({ videoId: video.id, mediaAssetId, vendorUploadId });
            }
          });
        });

        return {
          data: videos,
          pagination: {
            total: videos.length,
          },
        };
      }

      // If the search results was fetched from a product or tag search, we need to fetch the
      // video data for each video in the results.
      if (searchProductId || searchTagValue || searchActionId) {

        const fetchVideo = async ({ id } = {}) => {
          if (!id) throw new Error('Video id is required');
          const videoResponse = await fetch(`${vodUrl}/${basePath}/videos/${id}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${idToken}`,
            },
          });
          if (!videoResponse.ok) throw new Error('Failed to fetch video');
          const { data } = await videoResponse.json();
          return data;
        };

        const videos = body?.videos || body?.data?.videos || body?.data || [];

        videos.forEach((video) => {
          video.mediaAssets?.forEach(({ id: mediaAssetId, vendorAssetId, vendorUploadId }) => {
            if (vendorUploadId && !vendorAssetId) {
              handleProcessingMediaAsset({ videoId: video.id, mediaAssetId, vendorUploadId });
            }
          });
        });

        const populatedVideos = (await Promise.allSettled(videos.map(fetchVideo))).filter(({ status }) => status === 'fulfilled').map(({ value }) => value);
        return {
          data: populatedVideos,
          pagination: {
            total: populatedVideos.length,
          },
        };
      }

      return body;
    },
    keepPreviousData: true,
    ...options,
    enabled: !!orgId && (options.enabled ?? true),
  });
};

export default useVideosQuery;
